import { Box, Button, Container, LinearProgress, Typography } from '@mui/material';
import { AppForm, FormSubmitButton, SelectInput } from 'shared/components';
import { useTitle } from 'shared/hooks';
import { useProductMassAction, useUserActivityEvent } from 'shared/mutations';

import {
  CommoditySqlView,
  IdParameters,
  MassActionForm,
  MassActionSelection,
  RevisedBestPriceParameters,
  UserActivityEvent,
  UserInfo,
} from 'shared/types';
import { useProductState } from '../state';
import { DynamicPriceFields } from './dynamic-price-fields';

const MASS_ACTION_TYPES: MassActionSelection[] = [
  { type: 'percentage', display: 'Percentage' },
  { type: 'override', display: 'Override' },
  { type: 'adder', display: 'Adder' },
];

const DEFAULT_MASS_ACTION_FORM: MassActionForm = {
  type: null,
  amount: '',
};

interface MassActionPageProps {
  selectedProducts: CommoditySqlView[];
  user: UserInfo;
}
export function MassActionPage({ selectedProducts, user }: MassActionPageProps) {
  useTitle('Mass Action');
  const state = useProductState();
  const userTracking = useUserActivityEvent();
  const massActionMutation = useProductMassAction();

  const submit = async (form: MassActionForm) => {
    if (form.type) {
      const type = form.type.type;

      const ids = selectedProducts.map<IdParameters>(p => ({ id: p.id }));
      const massAction: RevisedBestPriceParameters = {
        type,
        amount: Number(form.amount),
        userId: user.id,
        userEmail: user.email,
      };
      await massActionMutation.mutateAsync({ ids, parameters: massAction });

      const activity: UserActivityEvent = {
        action: 'MassAction',
        eventType: 'ButtonClick',
        data: { actionType: type, itemsUpdated: selectedProducts.length },
      };
      userTracking.mutate(activity);
    }
  };

  const updating = massActionMutation.isLoading;
  const numberOfProducts = selectedProducts.length;
  return (
    <AppForm id='mass-action-form' defaultForm={DEFAULT_MASS_ACTION_FORM} onSubmit={submit}>
      {updating ? <LinearProgress /> : null}
      <Container sx={{ height: 'calc(100vh - 150px)' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', pt: 2, gap: 2 }}>
          <Typography variant='h5'>Updating {numberOfProducts} Products</Typography>
          <Box>
            <SelectInput
              name='type'
              label='Select Type'
              options={MASS_ACTION_TYPES}
              getOptionLabel={option => option.display}
              getOptionSelected={(option, value) => option.type === value.type}
              rules={{ required: { message: 'Please select a a mass action type to continue', value: true } }}
            />
          </Box>
          <Box>
            <DynamicPriceFields />
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Button
              variant='text'
              color='secondary'
              onClick={() => {
                state.selectedProducts.set([]);
              }}
            >
              cancel
            </Button>
            <FormSubmitButton submitText='continue' />
          </Box>
        </Box>
      </Container>
    </AppForm>
  );
}
